import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, CircularProgress, Alert, DialogContentText,
} from "@mui/material";
import { searchMarketplace, importFromMarketplace } from "../../../api/apiClient";
import { useConfigList } from "../../../hooks/useConfigList";
import { M, TYPE_LABELS, summaryLines } from "./marketplaceTokens";
import MarketplaceFacets from "./MarketplaceFacets";
import MarketplaceDetail from "./MarketplaceDetail";

const PAGE_SIZE = 24;
const DEFAULT_CATEGORIES = ["Firefighter", "EMS", "Hazmat", "Rescue", "Fire Officer", "General"];
const KNOWLEDGE_BANK_PATH = "/instructor/knowledge-bank";

function ResultRow({ listing, has, onOpen }) {
  const lines = summaryLines(listing.itemType, listing.contentSummary);
  return (
    <Box onClick={onOpen}
      sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "16px", alignItems: "center", p: "16px 18px", cursor: "pointer", "&:hover": { bgcolor: "#faf9fb" } }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".025em", textTransform: "uppercase", color: M.ink3 }}>{TYPE_LABELS[listing.itemType] || listing.itemType}</Box>
        <Typography sx={{ fontSize: "15.5px", fontWeight: 600, mt: "3px" }}>{listing.title}</Typography>
        {listing.description && (
          <Typography sx={{ fontSize: "13px", color: M.ink3, mt: "3px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{listing.description}</Typography>
        )}
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", mt: "8px", fontSize: "12.5px", color: M.ink3 }}>
          {listing.category && <Box component="span" sx={{ fontWeight: 700, px: "8px", py: "2px", borderRadius: "999px", bgcolor: M.surface2, color: M.ink2 }}>{listing.category}</Box>}
          {lines.map((l) => <span key={l}>{l}</span>)}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {has && <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "8px", py: "3px", borderRadius: "6px", bgcolor: M.successBg, color: M.success }}>In my bank</Box>}
        {has && listing.sourceContentVersion > listing.importedAtVersion && (
          <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "8px", py: "3px", borderRadius: "6px", bgcolor: M.warnBg, color: M.warn }}>Update available</Box>
        )}
        <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); onOpen(); }}>Details</Button>
      </Box>
    </Box>
  );
}

/** Full-page Marketplace — a real route (not a modal), so browsing and importing lives on its own screen. */
export default function MarketplaceView() {
  const navigate = useNavigate();
  const CATEGORIES = useConfigList("SKILL_CATEGORY", DEFAULT_CATEGORIES);
  const [q, setQ] = useState("");
  const [type, setType] = useState(undefined);
  const [category, setCategory] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [notice, setNotice] = useState(null);

  // Accumulated across this Marketplace visit so the Knowledge Bank page can refresh
  // and switch to the right tab once, when the user navigates back.
  const importsThisVisit = useRef([]);

  const load = useCallback(() => {
    setLoading(true);
    setPage(0);
    searchMarketplace({ type, q: q || undefined, category: category || undefined, page: 0, size: PAGE_SIZE })
      .then((r) => {
        const rows = Array.isArray(r) ? r : [];
        setResults(rows);
        setHasMore(rows.length === PAGE_SIZE);
        setLoading(false);
      })
      .catch(() => { setErr("Couldn't load marketplace listings."); setLoading(false); });
  }, [type, q, category]);

  const loadMore = () => {
    const next = page + 1;
    setLoadingMore(true);
    searchMarketplace({ type, q: q || undefined, category: category || undefined, page: next, size: PAGE_SIZE })
      .then((r) => {
        const rows = Array.isArray(r) ? r : [];
        setResults((prev) => [...prev, ...rows]);
        setPage(next);
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch(() => setErr("Couldn't load more listings."))
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => { load(); }, [load]);

  const selected = results.find((r) => r.id === selectedId) || null;

  const goBack = () => {
    navigate(KNOWLEDGE_BANK_PATH, { state: importsThisVisit.current.length ? { imports: importsThisVisit.current } : undefined });
  };

  const handleImport = async (listing, mode) => {
    setConfirming(null);
    setImportingId(listing.id);
    setErr(null);
    try {
      const imported = mode ? await importFromMarketplace(listing.id, listing.itemType, mode) : await importFromMarketplace(listing.id, listing.itemType);
      setSelectedId(null);
      // Flip the card to "In my bank" now; a later reload returns the same from the server.
      setResults((prev) => prev.map((r) => (r.id === listing.id
        ? { ...r, importedItemId: imported?.id ?? r.importedItemId ?? true, importedAtVersion: r.sourceContentVersion }
        : r)));
      const title = imported?.title || listing.title;
      importsThisVisit.current = [...importsThisVisit.current, { type: listing.itemType, title }];
      setNotice(`Imported "${title}" into your knowledge bank as a draft.`);
    } catch (e) {
      setErr(e?.response?.data?.message || "Import failed.");
    } finally {
      setImportingId(null);
    }
  };

  const requestImport = (listing) => (listing.importedItemId ? setConfirming(listing) : handleImport(listing));

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ mb: "4px" }}>
        <Button onClick={goBack} sx={{ textTransform: "none", pl: 0, mb: "8px", color: M.ink3 }}>← Back to Knowledge Bank</Button>
        <Typography sx={{ fontSize: { xs: "28px", md: "34px" }, fontWeight: 600, letterSpacing: "-.01em", color: M.ink, m: 0, fontFamily: "var(--font-display)" }}>Marketplace</Typography>
        <Box sx={{ fontSize: "14.5px", color: M.ink3, mt: "6px" }}>Browse content shared across the organization and add it to your knowledge bank.</Box>
      </Box>

      <Box sx={{ pt: 2 }}>
        <Box component="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, topic or description"
          sx={{ width: "100%", height: 44, px: "14px", border: `1.5px solid ${M.lineCool}`, borderRadius: "8px", font: "inherit", fontSize: "15px", "&:focus": { outline: "none", borderColor: M.primary } }} />
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mt: 2 }}>{notice}</Alert>}
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mt: 2 }}>{err}</Alert>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "200px minmax(0,1fr)" }, gap: "24px", pt: 2 }}>
        <MarketplaceFacets type={type} setType={setType} category={category} setCategory={setCategory} categories={CATEGORIES} />

        <Box sx={{ minWidth: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
          ) : results.length === 0 ? (
            <Typography sx={{ color: M.ink3, py: 4, textAlign: "center" }}>No marketplace content matches.</Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: "13px", color: M.ink3, mb: "10px" }}>{results.length} result{results.length === 1 ? "" : "s"}</Typography>
              <Box sx={{ border: `1px solid ${M.line}`, borderRadius: "10px", boxShadow: M.shadowSm, overflow: "hidden" }}>
                {results.map((listing, i) => (
                  <Box key={listing.id} sx={{ borderTop: i ? `1px solid ${M.line}` : "none" }}>
                    <ResultRow listing={listing} has={!!listing.importedItemId} onOpen={() => setSelectedId(listing.id)} />
                  </Box>
                ))}
              </Box>
              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                  <Button variant="outlined" size="small" disabled={loadingMore} onClick={loadMore}>{loadingMore ? "Loading…" : "Load more"}</Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {selected && (
        <MarketplaceDetail listing={selected} has={!!selected.importedItemId} importing={importingId === selected.id}
          onAdd={() => requestImport(selected)} onClose={() => setSelectedId(null)} />
      )}

      <Dialog open={!!confirming} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Import again?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You already have a copy of "{confirming?.title}". Replacing it discards any changes you made to it
            {confirming?.itemType === "CLASS_TEMPLATE" ? " and to the items it includes" : ""}; a new copy leaves your existing one untouched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button onClick={() => handleImport(confirming, "copy")}>Create a new copy</Button>
          <Button variant="contained" color="warning" onClick={() => handleImport(confirming, "overwrite")}>Replace my copy</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
